import { useCallback, useState } from 'react';

/**
 * Provenance-bucket visibility settings (brief 2026-07-22): two switches,
 * both OFF by default, persisted per analyst in ONE localStorage key shared
 * by the node drawer AND the drop-off drawer — the inline show/hide links and
 * the ⋯ settings popover flip the SAME state. The two drawers never render
 * simultaneously, so per-mount state + localStorage write-through suffices
 * (each mount reads fresh — PA-AC-004 persistence across drawer reopens).
 */

export const AUDIENCE_PROVENANCE_STORAGE_KEY = 'segmently:insights-audience-provenance:v1';

export interface AudienceProvenanceSettings {
  /** «Show answers given after this screen» (same visit). */
  showAfterScreen: boolean;
  /** «Show values from other visits» (earlier passes through the funnel). */
  showOtherVisits: boolean;
}

export const DEFAULT_AUDIENCE_PROVENANCE_SETTINGS: AudienceProvenanceSettings = {
  showAfterScreen: false,
  showOtherVisits: false,
};

export function readAudienceProvenanceSettings(): AudienceProvenanceSettings {
  try {
    const raw = window.localStorage.getItem(AUDIENCE_PROVENANCE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_AUDIENCE_PROVENANCE_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<AudienceProvenanceSettings> | null;
    // corrupt/foreign payloads degrade to the defaults, never throw (PA-AC-010)
    return {
      showAfterScreen: parsed?.showAfterScreen === true,
      showOtherVisits: parsed?.showOtherVisits === true,
    };
  } catch {
    return { ...DEFAULT_AUDIENCE_PROVENANCE_SETTINGS };
  }
}

export function writeAudienceProvenanceSettings(settings: AudienceProvenanceSettings): void {
  try {
    window.localStorage.setItem(AUDIENCE_PROVENANCE_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // storage unavailable (private mode / quota) — session-only state is fine
  }
}

export function useAudienceProvenanceSettings(): {
  settings: AudienceProvenanceSettings;
  setBucketShown: (bucket: 'after' | 'other', shown: boolean) => void;
} {
  const [settings, setSettings] = useState<AudienceProvenanceSettings>(readAudienceProvenanceSettings);
  const setBucketShown = useCallback((bucket: 'after' | 'other', shown: boolean) => {
    setSettings(previous => {
      const next = {
        ...previous,
        ...(bucket === 'after' ? { showAfterScreen: shown } : { showOtherVisits: shown }),
      };
      writeAudienceProvenanceSettings(next);
      return next;
    });
  }, []);
  return { settings, setBucketShown };
}
